import type { Block, BlockNoteEditor } from "@blocknote/core";
import { FirebaseService } from "@shared/lib/services/firebase.service";
import { collectBlocks } from "./editorBlocks";

const processingBlockIds = new Set<string>();
/** In-memory image bytes keyed by Firebase URL (for PDF/DOC export). */
const mediaCache = new Map<string, string>();
/** Native BlockNote uploadFile calls currently in flight. */
let mediaUploadsInFlight = 0;

export function isMediaUploadInFlight(): boolean {
  return mediaUploadsInFlight > 0 || processingBlockIds.size > 0;
}

function fileToDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Failed to read file"));
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export function cacheEditorMediaDataUrl(
  remoteUrl: string,
  dataUrl: string
): void {
  if (remoteUrl && dataUrl.startsWith("data:image/")) {
    mediaCache.set(remoteUrl, dataUrl);
  }
}

export function getCachedEditorMediaDataUrl(url: string): string | undefined {
  return mediaCache.get(url);
}

export function dataURLtoFile(dataurl: string, filename: string): File {
  const arr = dataurl.split(",");
  if (arr.length < 2 || !arr[1]) throw new Error("Invalid data URL");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  const ext = mime.split("/")[1] || "png";
  const name = filename.includes(".") ? filename : `${filename}.${ext}`;
  return new File([u8arr], name, { type: mime });
}

export async function blobURLtoFile(
  blobUrl: string,
  filename: string
): Promise<File> {
  const response = await fetch(blobUrl);
  if (!response.ok) throw new Error("Failed to read local media");
  const blob = await response.blob();
  const mime = blob.type || "image/png";
  const ext = mime.split("/")[1] || "png";
  const name = filename.includes(".") ? filename : `${filename}.${ext}`;
  return new File([blob], name, { type: mime });
}

export async function uploadEditorFileToFirebase(file: File): Promise<string> {
  mediaUploadsInFlight += 1;
  try {
    const timestamp = Date.now();
    const safeName = file.name
      ? file.name.replace(/[^a-zA-Z0-9._-]/g, "_")
      : "media";
    const fileToUpload = new File([file], `${timestamp}_${safeName}`, {
      type: file.type,
    });

    const remoteUrl = await FirebaseService.uploadFileToFirebase(
      "editor-media",
      fileToUpload
    );

    if (file.type.startsWith("image/")) {
      cacheEditorMediaDataUrl(remoteUrl, await fileToDataUrl(file));
    }

    return remoteUrl;
  } finally {
    mediaUploadsInFlight = Math.max(0, mediaUploadsInFlight - 1);
  }
}

export type ProcessMediaResult = {
  didUpload: boolean;
  document: Block[];
};

export function hasLocalMediaInEditor(editor: BlockNoteEditor): boolean {
  return collectBlocks(editor.document).some((block) => {
    if (block.type !== "image") return false;
    const url = (block.props as { url?: string } | undefined)?.url;
    return Boolean(url && (url.startsWith("data:") || url.startsWith("blob:")));
  });
}

export function waitForEditorMediaIdle(timeoutMs = 8000): Promise<void> {
  const startedAt = Date.now();
  return new Promise((resolve) => {
    const poll = () => {
      if (!isMediaUploadInFlight() || Date.now() - startedAt > timeoutMs) {
        resolve();
        return;
      }
      window.setTimeout(poll, 20);
    };
    poll();
  });
}

/** Upload local data:/blob: media and write Firebase URLs onto blocks. */
export async function processAndUploadMediaBlocks(
  editor: BlockNoteEditor
): Promise<ProcessMediaResult> {
  let didUpload = false;

  for (const block of collectBlocks(editor.document)) {
    if (block.type !== "image") continue;
    const props = block.props as { url?: string; name?: string } | undefined;
    const url = props?.url;
    if (!url || (!url.startsWith("data:") && !url.startsWith("blob:"))) continue;
    if (processingBlockIds.has(block.id)) continue;

    processingBlockIds.add(block.id);
    try {
      const localDataUrl = url.startsWith("data:image/") ? url : null;
      const file = localDataUrl
        ? dataURLtoFile(url, props.name ?? "uploaded_media")
        : await blobURLtoFile(url, props.name ?? "uploaded_media");

      const remoteUrl = await uploadEditorFileToFirebase(file);
      if (localDataUrl) cacheEditorMediaDataUrl(remoteUrl, localDataUrl);

      editor.updateBlock(block, {
        props: { ...props, url: remoteUrl, showPreview: true },
      });
      didUpload = true;
    } finally {
      processingBlockIds.delete(block.id);
    }
  }

  return { didUpload, document: editor.document };
}
